import { useWatch } from 'react-hook-form';
import { CameraList } from '../../components/CameraList';
import { useDeviceFormCtx } from '../DeviceFormProvider';
import { FetchSubDevicesQuery } from '@/lib/api/devices/devices';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  Camera, 
  Settings, 
  Plus, 
  Zap, 
  Volume2,
  Edit2,
  Save,
  X,
  Trash2} from 'lucide-react';
import { 
  CreateRelayMut, 
  UpdateRelayMut, 
  DeleteRelayMut,
  type CreateRelayInput,
  type UpdateRelayInput 
} from '@/lib/api/monitoring/relays';
import { 
  CreateSpeakerMut, 
  UpdateSpeakerMut, 
  DeleteSpeakerMut,
  type CreateSpeakerInput,
  type UpdateSpeakerInput 
} from '@/lib/api/monitoring/speakers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { DialogDescription } from '@radix-ui/react-dialog';
import { RelayComponentsModal } from '../components/RelayComponentsModal';
import { SpeakerComponentsModal } from '../components/SpeakerComponentsModal';

interface CamerasTabProps {
  deviceId: string;
  integrationType: string;
  projectId?: string;
}

interface DeviceCardProps {
  device: any;
  deviceId: string;
}

interface SingleComponentCardProps {
  deviceId: string;
  integrationType: string;
  parentDeviceId?: string;
}

const SingleComponentCard: React.FC<SingleComponentCardProps> = ({ deviceId, integrationType, parentDeviceId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isRelay = integrationType === 'Relay';
  const Icon = isRelay ? Zap : Volume2;
  const componentType = isRelay ? 'Relay' : 'Speaker';
  const iconColor = isRelay ? 'text-yellow-500' : 'text-green-500';

  const subDevicesQuery = useQuery({
    ...FetchSubDevicesQuery({ deviceId }),
  });
  const subDevices = subDevicesQuery.data ?? [];

  // Determine which sub-device this card should manage in the modal
  const targetSubDeviceId = useMemo(() => {
    const byType = subDevices.find((d: any) =>
      isRelay
        ? (Array.isArray(d?.relays) && d.relays.length > 0) || d?.integrationType?.toLowerCase() === 'relay'
        : (Array.isArray(d?.speakers) && d.speakers.length > 0) || d?.integrationType?.toLowerCase() === 'speaker'
    );
    // Fallback: first sub-device or current deviceId
    return byType?.id || subDevices[0]?.id || deviceId;
  }, [subDevices, isRelay, deviceId]);

  // Get component count
  const componentCount = subDevices.reduce((total, device) => {
    if (isRelay) {
      return total + (device.relays?.length || 0);
    } else {
      return total + (device.speakers?.length || 0);
    }
  }, 0);

  const handleComponentClick = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <Card className="cursor-pointer border transition-all duration-200 hover:border-gray-600 border-gray-700 bg-card2">
        <CardContent className="p-0">
          <div className="relative mb-3 flex aspect-video items-center justify-center overflow-hidden bg-gray-800">
            <Icon className={`h-16 w-16 ${iconColor} opacity-50`} />
          </div>

          <div className="space-y-2 px-2 pb-4">
            <div className="flex items-center justify-between">
              <h3 className="truncate text-lg font-semibold text-white">
                {componentType} Device
              </h3>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 flex-shrink-0"
                onClick={handleComponentClick}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-center">
                <div 
                  className="flex items-center gap-1 cursor-pointer hover:bg-gray-700/50 rounded px-2 py-1 transition-colors"
                  onClick={handleComponentClick}
                >
                  <Icon className={`h-4 w-4 ${iconColor}`} />
                  <span className="text-sm text-gray-300">{componentCount}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Component Management Modal */}
      {isRelay ? (
        <RelayComponentsModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          onClose={() => setIsModalOpen(false)}
          deviceId={parentDeviceId || deviceId}
          subDeviceId={targetSubDeviceId}
        />
      ) : (
        <SpeakerComponentsModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          onClose={() => setIsModalOpen(false)}
          deviceId={parentDeviceId || deviceId}
          subDeviceId={targetSubDeviceId}
        />
      )}
    </>
  );
};

const NVRDeviceCard = ({ device, deviceId }: DeviceCardProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [isAdding, setIsAdding] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string; type: string } | null>(null);

  const queryClient = useQueryClient();
  const relays = device.relays || [];
  const lenses = device.lenses || [];
  const speakers = device.speakers || [];

  // Mutations (only for relays for now - TODO: add for lenses and speakers)
  const createRelayMut = useMutation({
    ...CreateRelayMut(),
    onMutate: async (newRelay: CreateRelayInput) => {
      await queryClient.cancelQueries({ queryKey: ['device.secondary', deviceId] });
      const previousData = queryClient.getQueryData(['device.secondary', deviceId]);

      queryClient.setQueryData(['device.secondary', deviceId], (old: any) => {
        if (!old) return old;
        
        return old.map((d: any) => {
          if (d.id === device.id) {
            return {
              ...d,
              relays: [
                ...(d.relays || []),
                { id: `temp_${Date.now()}`, name: newRelay.name }
              ]
            };
          }
          return d;
        });
      });

      return { previousData };
    },
    onError: (err, _newRelay, context) => {
      queryClient.setQueryData(['device.secondary', deviceId], context?.previousData);
      toast({
        title: 'Failed to create relay',
        description: err.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      setIsAdding(null);
      setNewName('');
      toast({ title: 'Relay created', description: 'Relay component created successfully.' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['device.secondary', deviceId] });
    },
  });

  const updateRelayMut = useMutation({
    ...UpdateRelayMut(),
    onMutate: async (updatedRelay: UpdateRelayInput) => {
      await queryClient.cancelQueries({ queryKey: ['device.secondary', deviceId] });
      const previousData = queryClient.getQueryData(['device.secondary', deviceId]);

      queryClient.setQueryData(['device.secondary', deviceId], (old: any) => {
        if (!old) return old;
        
        return old.map((d: any) => {
          if (d.id === device.id) {
            return {
              ...d,
              relays: (d.relays || []).map((relay: any) => 
                relay.id === updatedRelay.id 
                  ? { ...relay, name: updatedRelay.name }
                  : relay
              )
            };
          }
          return d;
        });
      });

      return { previousData };
    },
    onError: (err, _updatedRelay, context) => {
      queryClient.setQueryData(['device.secondary', deviceId], context?.previousData);
      toast({
        title: 'Failed to update relay',
        description: err.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      setEditingId(null);
      setEditName('');
      toast({ title: 'Relay updated', description: 'Relay component updated successfully.' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['device.secondary', deviceId] });
    },
  });

  const deleteRelayMut = useMutation({
    ...DeleteRelayMut(),
    onMutate: async (deletedRelay: { id: string }) => {
      await queryClient.cancelQueries({ queryKey: ['device.secondary', deviceId] });
      const previousData = queryClient.getQueryData(['device.secondary', deviceId]);

      queryClient.setQueryData(['device.secondary', deviceId], (old: any) => {
        if (!old) return old;
        
        return old.map((d: any) => {
          if (d.id === device.id) {
            return {
              ...d,
              relays: (d.relays || []).filter((relay: any) => relay.id !== deletedRelay.id)
            };
          }
          return d;
        });
      });

      return { previousData };
    },
    onError: (err, _deletedRelay, context) => {
      queryClient.setQueryData(['device.secondary', deviceId], context?.previousData);
      toast({
        title: 'Failed to delete relay',
        description: err.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({ title: 'Relay deleted', description: 'Relay component deleted successfully.' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['device.secondary', deviceId] });
    },
  });

  // Speaker mutations
  const createSpeakerMut = useMutation({
    ...CreateSpeakerMut(),
    onMutate: async (newSpeaker: CreateSpeakerInput) => {
      await queryClient.cancelQueries({ queryKey: ['device.secondary', deviceId] });
      const previousData = queryClient.getQueryData(['device.secondary', deviceId]);

      queryClient.setQueryData(['device.secondary', deviceId], (old: any) => {
        if (!old) return old;
        
        return old.map((d: any) => {
          if (d.id === device.id) {
            return {
              ...d,
              speakers: [
                ...(d.speakers || []),
                { id: `temp_${Date.now()}`, name: newSpeaker.name, componentGroupId: newSpeaker.componentGroupId }
              ]
            };
          }
          return d;
        });
      });

      return { previousData };
    },
    onError: (err, _newSpeaker, context) => {
      queryClient.setQueryData(['device.secondary', deviceId], context?.previousData);
      toast({
        title: 'Failed to create speaker',
        description: err.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      setIsAdding(null);
      setNewName('');
      toast({ title: 'Speaker created', description: 'Speaker component created successfully.' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['device.secondary', deviceId] });
    },
  });

  const updateSpeakerMut = useMutation({
    ...UpdateSpeakerMut(),
    onMutate: async (updatedSpeaker: UpdateSpeakerInput) => {
      await queryClient.cancelQueries({ queryKey: ['device.secondary', deviceId] });
      const previousData = queryClient.getQueryData(['device.secondary', deviceId]);

      queryClient.setQueryData(['device.secondary', deviceId], (old: any) => {
        if (!old) return old;
        
        return old.map((d: any) => {
          if (d.id === device.id) {
            return {
              ...d,
              speakers: (d.speakers || []).map((speaker: any) => 
                speaker.id === updatedSpeaker.id 
                  ? { ...speaker, name: updatedSpeaker.name }
                  : speaker
              )
            };
          }
          return d;
        });
      });

      return { previousData };
    },
    onError: (err, _updatedSpeaker, context) => {
      queryClient.setQueryData(['device.secondary', deviceId], context?.previousData);
      toast({
        title: 'Failed to update speaker',
        description: err.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      setEditingId(null);
      setEditName('');
      toast({ title: 'Speaker updated', description: 'Speaker component updated successfully.' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['device.secondary', deviceId] });
    },
  });

  const deleteSpeakerMut = useMutation({
    ...DeleteSpeakerMut(),
    onMutate: async (deletedSpeaker: { id: string }) => {
      await queryClient.cancelQueries({ queryKey: ['device.secondary', deviceId] });
      const previousData = queryClient.getQueryData(['device.secondary', deviceId]);

      queryClient.setQueryData(['device.secondary', deviceId], (old: any) => {
        if (!old) return old;
        
        return old.map((d: any) => {
          if (d.id === device.id) {
            return {
              ...d,
              speakers: (d.speakers || []).filter((speaker: any) => speaker.id !== deletedSpeaker.id)
            };
          }
          return d;
        });
      });

      return { previousData };
    },
    onError: (err, _deletedSpeaker, context) => {
      queryClient.setQueryData(['device.secondary', deviceId], context?.previousData);
      toast({
        title: 'Failed to delete speaker',
        description: err.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({ title: 'Speaker deleted', description: 'Speaker component deleted successfully.' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['device.secondary', deviceId] });
    },
  });

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      if (deleteConfirm.type === 'relay') {
        deleteRelayMut.mutate({ id: deleteConfirm.id });
      } else if (deleteConfirm.type === 'speaker') {
        deleteSpeakerMut.mutate({ id: deleteConfirm.id });
      }
    }
    setDeleteConfirm(null);
  };

  const ComponentRow = ({ component, type, icon: Icon }: { component: any; type: string; icon: any }) => {
    const isEditing = editingId === component.id;
    
    return (
      <div className="flex items-center justify-between p-2 rounded border border-gray-700 bg-gray-800/30">
        <div className="flex items-center gap-2 flex-1">
          <Icon className={`h-4 w-4 ${
            type === 'relay' ? 'text-yellow-500' 
            : type === 'speaker' ? 'text-blue-500' 
            : 'text-gray-500'
          }`} />
          {isEditing ? (
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="h-7 text-sm"
              autoFocus
            />
          ) : (
            <span className="text-sm font-medium">{component.name}</span>
          )}
        </div>
        <div className="flex gap-1">
          {isEditing ? (
            <>
              <Button
                size="sm"
                onClick={() => {
                  if (type === 'relay') {
                    updateRelayMut.mutate({ 
                      id: component.id, 
                      name: editName.trim(),
                      componentGroupId: component.componentGroupId,
                      relatedComponentGroups: component.relatedComponentGroups || [],
                    });
                  } else if (type === 'speaker') {
                    updateSpeakerMut.mutate({ 
                      id: component.id, 
                      name: editName.trim(),
                      componentGroupId: component.componentGroupId,
                      relatedComponentGroups: component.relatedComponentGroups || [],
                    });
                  }
                }}
                className="h-7 w-7 p-0 bg-lime text-black hover:bg-lime/80"
              >
                <Save className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setEditingId(null); setEditName(''); }}
                className="h-7 w-7 p-0 text-gray-400 hover:text-white"
              >
                <X className="h-3 w-3" />
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                onClick={() => { setEditingId(component.id); setEditName(component.name); }}
                className="h-7 w-7 p-0 bg-lime text-black hover:bg-lime/80"
              >
                <Edit2 className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDeleteConfirm({ id: component.id, name: component.name, type })}
                className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  const AddComponentRow = ({ type, icon: Icon, placeholder }: { type: string; icon: any; placeholder: string }) => (
    <div className="flex items-center gap-2 p-2 rounded border border-gray-600 bg-gray-700/30">
      <Icon className={`h-4 w-4 ${
        type === 'relay' ? 'text-yellow-500' 
        : type === 'speaker' ? 'text-blue-500' 
        : 'text-gray-500'
      }`} />
      <Input
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        placeholder={placeholder}
        className="h-7 text-sm flex-1"
        autoFocus
      />
      <div className="flex gap-1">
        <Button
          size="sm"
          onClick={() => {
            if (newName.trim()) {
              if (type === 'relay') {
                createRelayMut.mutate({
                  name: newName.trim(),
                  componentGroupId: device.id
                });
              } else if (type === 'speaker') {
                createSpeakerMut.mutate({
                  name: newName.trim(),
                  componentGroupId: device.id
                });
              }
            }
          }}
          className="h-7 w-7 p-0 bg-lime text-black hover:bg-lime/80"
        >
          <Save className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => { setIsAdding(null); setNewName(''); }}
          className="h-7 w-7 p-0 text-gray-400 hover:text-white"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );

  const ComponentSection = ({ components, type, icon, label, placeholder }: { 
    components: any[]; 
    type: string; 
    icon: any; 
    label: string;
    placeholder: string;
  }) => (
    <AccordionItem value={type}>
      <AccordionTrigger className="text-sm font-medium py-2">
        <div className="flex items-center gap-2">
          {React.createElement(icon, { className: "h-4 w-4" })}
          {label} ({components.length})
        </div>
      </AccordionTrigger>
      <AccordionContent className="space-y-2 pt-2">
        {components.map((component) => (
          <ComponentRow key={component.id} component={component} type={type} icon={icon} />
        ))}
        {isAdding === type ? (
          <AddComponentRow type={type} icon={icon} placeholder={placeholder} />
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsAdding(type)}
            className="w-full h-8 text-lime hover:text-lime/80 hover:bg-lime/10 border border-dashed border-gray-600"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add {label}
          </Button>
        )}
      </AccordionContent>
    </AccordionItem>
  );

  return (
    <>
      <Card className="bg-[#1a1a1a] border-gray-800">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-blue-500" />
            <CardTitle className="text-base">{device.name || 'Unnamed NVR'}</CardTitle>
          </div>
          <div className="text-sm text-gray-400 mt-1">
            {device.model && <span>Model: {device.model}</span>}
            {device.vendor && <span className="ml-4">Vendor: {device.vendor}</span>}
          </div>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" className="w-full">
            <ComponentSection
              components={relays}
              type="relay"
              icon={Zap}
              label="Relays"
              placeholder="Enter relay name"
            />
            <ComponentSection
              components={lenses}
              type="lens"
              icon={Camera}
              label="Lenses"
              placeholder="Enter lens name"
            />
            <ComponentSection
              components={speakers}
              type="speaker"
              icon={Volume2}
              label="Speakers"
              placeholder="Enter speaker name"
            />
          </Accordion>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete {deleteConfirm?.type}</DialogTitle>
            <DialogDescription className="text-gray-400 space-y-2">
              <p>Are you sure you want to delete <span className="font-medium text-white">"{deleteConfirm?.name}"</span>?</p>
              <p>This action cannot be undone.</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const CamerasTab = (props: CamerasTabProps) => {
  const form = useDeviceFormCtx();
  const monitoredDevices = useWatch({
    control: form.control,
    name: 'monitoredDevices',
  });
  const subDevicesQuery = useQuery({
    ...FetchSubDevicesQuery({ deviceId: props.deviceId }),
  });
  const subDevices = subDevicesQuery.data ?? [];

  // Check if this is an NVR device
  const isNVR = props.integrationType === 'Nvr';

  // Filter for NVR sub-devices
  const nvrDevices = subDevices;

  const handleCameraToggle = (cameraId: string) => {
    const existingArr = form.getValues('monitoredDevices');

    if (!existingArr[cameraId]) {
      form.setValue(`monitoredDevices.${cameraId}`, true, { shouldDirty: true });
      return;
    }

    form.setValue(`monitoredDevices.${cameraId}`, false, { shouldDirty: true });
  };

  const handleCameraToggleMany = (cameraIds: string[], enabled: boolean) => {
    if (enabled) {
      const values = { ...form.getValues('monitoredDevices') };

      for (const id of cameraIds) {
        values[id] = true;
      }
      form.setValue('monitoredDevices', values, { shouldDirty: true });
      return;
    }

    const values = { ...form.getValues('monitoredDevices') };

    for (const id of cameraIds) {
      values[id] = false;
    }

    form.setValue('monitoredDevices', values, { shouldDirty: true });
  };

  const camerasWithMonitoringState = useMemo(
    () =>
      subDevices.map((camera) => {
        const isEnabled = camera.id ? monitoredDevices[camera.id] : false;

        return {
          ...camera,
          monitored: isEnabled,
        };
      }),
    [subDevices, monitoredDevices]
  );

  if (subDevicesQuery.isLoading) {
    return (
      <div className="flex flex-col gap-y-2 px-4">
        <Skeleton className="my-8 h-[50px] w-full" />
        {Array.from({ length: 5 }).map((_, i) => {
          return <Skeleton key={i} className="h-[100px] w-full" />;
        })}
      </div>
    );
  }

  // For Relay and Speaker devices, show a single component card
  if (props.integrationType === 'Relay' || props.integrationType === 'Speaker') {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <SingleComponentCard 
            deviceId={props.deviceId}
            integrationType={props.integrationType}
            parentDeviceId={props.deviceId}
          />
        </div>
      </div>
    );
  }

  // For NVR and other devices, use CameraList
  return (
    <CameraList
      cameras={camerasWithMonitoringState}
      onCameraToggle={handleCameraToggle}
      deviceId={props.deviceId}
      projectId={props.projectId}
      onCameraToggleMany={handleCameraToggleMany}
    />
  );
};
